import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Button,
  useDisclosure,
  Spinner,
  Center,
  Alert,
  AlertIcon,
  AlertDescription
} from '@chakra-ui/react';
import { MdOutlineVisibility } from 'react-icons/md';

interface PdfViewerIframeProps {
  pdfUrl: string;
  buttonLabel?: string;
  modalTitle?: string;
}

/**
 * Componente para visualizar PDF em modal usando iframe nativo
 * Vantagens:
 * - Totalmente independente, sem dependências externas
 * - Rápido e leve
 * - Funciona offline se o PDF já estiver carregado
 * 
 * Desvantagens:
 * - Suporte ao visualizador depende do navegador
 * - Alguns navegadores podem ter limitações de CORS
 * - Interface de controles depende do navegador
 */
export const PdfViewerIframe: React.FC<PdfViewerIframeProps> = ({
  pdfUrl,
  buttonLabel = 'Visualizar PDF',
  modalTitle = 'Visualização do PDF'
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  const handleOpen = () => {
    setIsLoading(true);
    setHasError(false);
    onOpen();
  };

  const handleLoadError = () => {
    setIsLoading(false);
    setHasError(true);
  };

  const handleLoadSuccess = () => {
    setIsLoading(false);
    setHasError(false);
  };

  return (
    <>
      <Button
        leftIcon={<MdOutlineVisibility />}
        colorScheme="blue"
        variant="outline"
        size="sm"
        mb="16px"
        onClick={handleOpen}
      >
        {buttonLabel}
      </Button>

      <Modal isOpen={isOpen} onClose={onClose} size="full" isCentered>
        <ModalOverlay />
        <ModalContent 
          maxWidth="95vw"
          maxHeight="95vh"
          height="95vh"
          display="flex"
          flexDirection="column"
        >
          <ModalHeader fontSize="lg" fontWeight="600">{modalTitle}</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6} display="flex" flexDirection="column" flex={1} overflow="hidden">
            {isLoading && (
              <Center height="100%">
                <Spinner
                  thickness="4px"
                  speed="0.65s"
                  emptyColor="gray.200"
                  color="blue.500"
                  size="xl"
                />
              </Center>
            )}

            {hasError && (
              <Alert
                status="warning"
                variant="subtle"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                textAlign="center"
                height="100%"
                borderRadius="md"
              >
                <AlertIcon boxSize="40px" mr={0} />
                <AlertDescription mt={4} maxWidth="sm">
                  Não foi possível carregar o visualizador de PDF nativo do navegador.
                  <br />
                  <Button
                    mt={4}
                    as="a"
                    href={pdfUrl}
                    target="_blank"
                    colorScheme="blue"
                    size="sm"
                  >
                    Baixar PDF
                  </Button>
                </AlertDescription>
              </Alert>
            )}

            {!hasError && (
              <iframe
                src={pdfUrl}
                title="PDF Viewer"
                width="100%"
                height="100%"
                style={{
                  border: 'none',
                  display: isLoading || hasError ? 'none' : 'block',
                  borderRadius: '4px'
                }}
                onLoad={handleLoadSuccess}
                onError={handleLoadError}
              />
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};
